import { Link, router } from '@inertiajs/react'
import { useState, useMemo } from 'react'
import { Layout } from '@/components/Layout'
import { PageHead } from '@/components/PageHead'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Plus, Pencil, Trash2, Building2, Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { DateRangePicker } from '@/components/DateRangePicker'

interface Client {
  id: string
  name: string
  code: string
  instances_count: number
  total_executions: number
  failures: number
  success_rate: number
  time_saved_minutes: number
  time_saved_hours: number
}

interface Pagination {
  page: number
  count: number
  pages: number
  prev: number | null
  next: number | null
}

interface DateRange {
  start_date: string
  end_date: string
}

interface Props {
  clients: Client[]
  pagination: Pagination
  search: string
  date_range: DateRange
}

type SortKey = 'name' | 'code' | 'instances_count' | 'total_executions' | 'failures' | 'success_rate' | 'time_saved_minutes'
type SortDirection = 'asc' | 'desc'

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

function formatTimeSaved(hours: number, minutes: number): string {
  if (hours >= 1) return `${hours}h`
  return `${minutes}m`
}

export default function ClientsIndex({ clients, pagination, search, date_range }: Props) {
  const [searchInput, setSearchInput] = useState(search)
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const sortedClients = useMemo(() => {
    if (!sortKey) return clients
    return [...clients].sort((a, b) => {
      const aVal = a[sortKey]
      const bVal = b[sortKey]
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      return sortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [clients, sortKey, sortDirection])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
    return sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
  }

  const handleDelete = (id: string) => {
    router.delete(`/clients/${id}`)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get('/clients', {
      search: searchInput,
      start_date: date_range.start_date,
      end_date: date_range.end_date,
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handlePageChange = (page: number) => {
    router.get('/clients', {
      page,
      search: search,
      start_date: date_range.start_date,
      end_date: date_range.end_date,
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleDateRangeChange = (range: { from: Date; to: Date }) => {
    router.get('/clients', {
      search: search,
      start_date: range.from.toISOString().split('T')[0],
      end_date: range.to.toISOString().split('T')[0],
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const dateValue = {
    from: new Date(date_range.start_date),
    to: new Date(date_range.end_date),
  }

  return (
    <Layout>
      <PageHead title="Clients" description="Manage client organizations and their n8n instances." />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Clients</h1>
            <p className="text-muted-foreground">
              Manage your client organizations and their n8n instances.
            </p>
          </div>
          <Button asChild>
            <Link href="/clients/new">
              <Plus className="mr-2 h-4 w-4" />
              Add Client
            </Link>
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-4">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search clients..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-64 pl-8"
              />
            </div>
            <Button type="submit" variant="secondary" size="sm">
              Search
            </Button>
          </form>
          <div className="ml-auto">
            <DateRangePicker value={dateValue} onChange={handleDateRangeChange} />
          </div>
        </div>

        {clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12">
            <Building2 className="h-12 w-12 text-muted-foreground" />
            <h2 className="mt-4 text-xl font-semibold">
              {search ? 'No clients found' : 'No clients yet'}
            </h2>
            <p className="mt-2 text-center text-muted-foreground">
              {search
                ? `No clients match "${search}". Try a different search term.`
                : 'Get started by adding your first client organization.'}
            </p>
            {!search && (
              <Button asChild className="mt-4">
                <Link href="/clients/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Client
                </Link>
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button onClick={() => handleSort('name')} className="flex items-center font-medium hover:text-foreground">
                        Name <SortIcon column="name" />
                      </button>
                    </TableHead>
                    <TableHead className="text-center">
                      <button onClick={() => handleSort('instances_count')} className="flex items-center justify-center font-medium hover:text-foreground w-full">
                        Instances <SortIcon column="instances_count" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('total_executions')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                        Executions <SortIcon column="total_executions" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('failures')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                        Failures <SortIcon column="failures" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('success_rate')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                        Success Rate <SortIcon column="success_rate" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('time_saved_minutes')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                        Time Saved <SortIcon column="time_saved_minutes" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedClients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell>
                        <Link
                          href={`/clients/${client.id}`}
                          className="font-medium hover:underline"
                        >
                          {client.name}
                        </Link>
                        <p className="text-xs text-muted-foreground">
                          {client.code}
                        </p>
                      </TableCell>
                      <TableCell className="text-center">
                        {client.instances_count}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(client.total_executions)}
                      </TableCell>
                      <TableCell className="text-right">
                        {client.failures > 0 ? (
                          <Badge variant="destructive">{client.failures}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={client.success_rate < 80 ? 'text-destructive font-medium' : ''}>
                          {client.total_executions > 0 ? `${client.success_rate}%` : '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {client.time_saved_hours > 0 || client.time_saved_minutes > 0
                          ? formatTimeSaved(client.time_saved_hours, client.time_saved_minutes)
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link href={`/clients/${client.id}/edit`}>
                              <Pencil className="h-4 w-4" />
                              <span className="sr-only">Edit</span>
                            </Link>
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <Trash2 className="h-4 w-4" />
                                <span className="sr-only">Delete</span>
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete client?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete {client.name} and all
                                  associated n8n instances, workflows, and execution
                                  history. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(client.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing page {pagination.page} of {pagination.pages} ({pagination.count} total clients)
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.prev}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.next}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  )
}
